import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import ValidationService from "../../../../Services/Shared/Validation.service";
import FAIcon from "../../../UI/FAIcon/FAIcon";
import useService from "../../../Hooks/useService";
import MessageService from "../../../../Services/Message.service";
import DeprecatedSelect from "../../../UI/DeprecatedSelect/DeprecatedSelect";
import useUniqueId from "../../../Hooks/useUniqueId";
import useConfiguration from "../../../Hooks/useConfiguration";
import { AlertInfo, AlertWarning, } from "../../../UI/StandardAlerts/StandardAlerts";
import NumberService from "../../../../Services/Shared/Number.service";
import ListService from "../../../../Services/Shared/List.service";
import "react-rrule-generator/build/styles.css";
import CustomCard from "../../../UI/CustomCard/CustomCard";
import FromEmailAddressDomainService from "../../../../Domain/FromEmailAddress/FromEmailAddress.service";
import CampaignSendScheduleForm from "../../../Presentational/Campaign/CampaignSendScheduleForm";
import AdHocCaseloadField from "../../../Presentational/Filter/AdHocCaseloadField";
import SearchField from "../../../Form/SearchField";
import { PersonPopulationSearch } from "../../../Presentational/Population/PersonPopulationSearch";
import useCurrentApiUser from "../../../Hooks/useCurrentApiUser";
import PopulationService from "../../../../Services/Population.service";
import { RadioField } from "../../../Form/RadioField";
const getFromHelpText = (address) => {
    switch (address.type) {
        case FromEmailAddressDomainService.Types.DelegateFromEmailAddress:
            return `Messages will show up in the ${address.person.fullName}'s inbox and sentbox, but only after a recipient replies to the message`;
        case FromEmailAddressDomainService.Types.DirectFromEmailAddress:
            return `Replies will be forwarded directly to ${address.fromEmail}, and will not be shown in Aviso.`;
        case FromEmailAddressDomainService.Types.NoReplyFromEmailAddress:
            return `Replies will be ignored. They will not be sent to ${address.fromEmail}.`;
        default:
            return "";
    }
};
const CampaignFormMessageSettings = (props) => {
    var _a;
    const { register, errors, triggerValidation, formState, watch } = useFormContext();
    const [fromOptionsState, loadFromOptions] = useService(MessageService.getFroms);
    const makeId = useUniqueId()[1];
    const population = watch("population");
    const populationId = population === null || population === void 0 ? void 0 : population.id;
    const populationAdHocCaseloads = watch("populationAdHocCaseloads");
    const maxStudentsForCampaign = parseInt((_a = useConfiguration("maxCampaignRecipients")) !== null && _a !== void 0 ? _a : "0", 10);
    const currentUser = useCurrentApiUser();
    const [numberOfStudentsInPopulation, loadNumberOfStudentsInPopulation] = useService(PopulationService.countForPersonPopulation);
    React.useEffect(() => {
        if (populationId &&
            populationAdHocCaseloads != null &&
            currentUser != null) {
            loadNumberOfStudentsInPopulation(populationId, {
                tempVals: ListService.isEmptyArray(populationAdHocCaseloads)
                    ? {}
                    : {
                        inPersonsCaseload: populationAdHocCaseloads.map(({ personId }) => personId),
                    },
            });
        }
    }, [
        currentUser,
        loadNumberOfStudentsInPopulation,
        populationAdHocCaseloads,
        populationId,
    ]);
    React.useEffect(() => {
        loadFromOptions();
    }, [loadFromOptions]);
    const sendBehavior = watch("sendBehavior");
    const { isSubmitted } = formState;
    React.useEffect(() => {
        if (isSubmitted) {
            triggerValidation();
        }
    }, [sendBehavior, isSubmitted, triggerValidation]);
    const groupedOptions = ListService.groupByString(fromOptionsState.data || [], (item) => item.type);
    const from = watch("from");
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group required", children: [_jsx("label", { htmlFor: makeId("name"), children: "Name" }), _jsx("input", { id: makeId("name"), type: "text", name: "name", className: ValidationService.makeClassName(errors, "name", "form-control"), ref: register({ required: "Name is required" }) }), _jsx("div", { className: "invalid-feedback", children: "Name is required" })] }), _jsxs("div", { className: "form-group required", children: [_jsx("label", { htmlFor: makeId("from"), children: "From" }), _jsx(Controller, { name: "from", rules: { required: "From is required" }, as: _jsx(DeprecatedSelect, { "aria-label": "From", id: makeId("from"), className: ValidationService.makeClassName(errors, "from"), isLoading: fromOptionsState.isLoading, options: Object.keys(groupedOptions).map((key) => ({
                                label: key === "Person" ? "Person" : "Shared Email",
                                options: groupedOptions[key],
                            })), valueKey: "id", labelKey: "label" }) }), _jsx("div", { className: "invalid-feedback", children: "From is required" })] }), (from === null || from === void 0 ? void 0 : from.type) === "FromEmailAddress" && (_jsx(AlertInfo, { message: getFromHelpText(from.value) })), maxStudentsForCampaign != null &&
                numberOfStudentsInPopulation.data != null &&
                numberOfStudentsInPopulation.data > maxStudentsForCampaign && (_jsx(AlertWarning, { isLoading: numberOfStudentsInPopulation.isLoading, message: `Campaigns can only be sent to filters with less than ${NumberService.format(maxStudentsForCampaign)} students.  This filter has ${NumberService.format(numberOfStudentsInPopulation.data)} students.` })), _jsx(CustomCard, { noShadow: true, className: "mb-3", children: _jsxs("div", { className: "card-body", children: [_jsx(SearchField, { label: "To", name: "population", as: (provided) => (_jsx(PersonPopulationSearch, Object.assign({ detailButtonProps: {
                                    populationAdHocCaseloads,
                                }, adHoc: false, additionalOptions: props.populationsToAddAsOptions }, provided))), required: true }), _jsx(AdHocCaseloadField, { name: "populationAdHocCaseloads", population: population })] }) }), _jsx(RadioField, { name: "useInstitutionalEmail", options: [
                    {
                        label: (_jsxs(_Fragment, { children: [_jsx(FAIcon, { icon: "university", addMarginRight: true }), "Institutional Email"] })),
                        value: "true",
                    },
                    {
                        label: (_jsxs(_Fragment, { children: [_jsx(FAIcon, { icon: "user", addMarginRight: true }), "Personal Email"] })),
                        value: "false",
                    },
                ], helpText: "In order to accurately track a user's progression through the campaign, only one email can be used per person." }), _jsx(CustomCard, { title: "Send Schedule", children: _jsx("div", { className: "card-body", children: _jsx(CampaignSendScheduleForm, { isEditing: false }) }) })] }));
};
export default CampaignFormMessageSettings;
